import { useState, useEffect } from 'preact/hooks'
import { 
  forceCacheRefresh, 
  clearAllCaches, 
  getCacheInfo, 
  forceReload, 
  nuclearReset,
  browserCache 
} from '../utils/cacheUtils'

export function DebugPanel({ onClose }) {
  const [cacheInfo, setCacheInfo] = useState(null)
  const [storageInfo, setStorageInfo] = useState(null)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')

  // Load cache and storage info on mount
  useEffect(() => {
    loadInfo()
  }, [])

  const loadInfo = async () => {
    try {
      const [cache, storage] = await Promise.all([
        getCacheInfo(),
        browserCache.getStorageInfo()
      ])
      setCacheInfo(cache)
      setStorageInfo(storage)
    } catch (error) {
      console.error('Failed to load debug info:', error)
    }
  }

  const handleAction = async (action, actionName) => {
    setLoading(true)
    setMessage('')
    
    try {
      const result = await action()
      setMessage(`${actionName}: ${result.message}`)
      
      // Reload info after cache operations
      if (actionName.includes('Cache')) {
        setTimeout(loadInfo, 1000)
      }
    } catch (error) {
      setMessage(`${actionName} failed: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-editions-dark border border-editions-purple/30 rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            🛠️ Debug Panel
            <span className="text-sm text-editions-orange">(Dev Mode)</span>
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
            aria-label="Close debug panel"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Cache Information */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-white mb-3">Cache Information</h3>
          <div className="bg-editions-dark-light/50 rounded-lg p-4 space-y-2">
            {cacheInfo ? (
              <>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-300">Cache Version:</span>
                  <span className="text-editions-purple font-mono">{cacheInfo.version}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-300">Static Assets:</span>
                  <span className="text-white">{cacheInfo.staticAssets} files</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-300">Data Assets:</span>
                  <span className="text-white">{cacheInfo.dataAssets} files</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-300">Total Cached:</span>
                  <span className="text-editions-green">{cacheInfo.totalSize} files</span>
                </div>
              </>
            ) : (
              <div className="text-gray-400 text-sm">Loading cache info...</div>
            )}
          </div>
        </div>

        {/* Storage Information */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-white mb-3">Storage Information</h3>
          <div className="bg-editions-dark-light/50 rounded-lg p-4 space-y-2">
            {storageInfo?.success ? (
              <>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-300">localStorage:</span>
                  <span className="text-white">{formatBytes(storageInfo.info.localStorage)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-300">sessionStorage:</span>
                  <span className="text-white">{formatBytes(storageInfo.info.sessionStorage)}</span>
                </div>
                {storageInfo.info.quota && (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-300">Storage Used:</span>
                      <span className="text-white">{formatBytes(storageInfo.info.usage)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-300">Storage Quota:</span>
                      <span className="text-white">{formatBytes(storageInfo.info.quota)}</span>
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="text-gray-400 text-sm">Loading storage info...</div>
            )}
          </div>
        </div>

        {/* Cache Management Actions */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-white mb-3">Cache Management</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              onClick={() => handleAction(forceCacheRefresh, 'Force Cache Refresh')}
              disabled={loading}
              className="px-4 py-3 bg-editions-purple/20 hover:bg-editions-purple/30 
                         border border-editions-purple/40 text-editions-purple-light
                         rounded-lg font-medium transition-all duration-200
                         disabled:opacity-50 disabled:cursor-not-allowed
                         text-sm"
            >
              🔄 Refresh Cache
            </button>
            
            <button
              onClick={() => handleAction(clearAllCaches, 'Clear All Caches')}
              disabled={loading}
              className="px-4 py-3 bg-editions-orange/20 hover:bg-editions-orange/30 
                         border border-editions-orange/40 text-editions-orange
                         rounded-lg font-medium transition-all duration-200
                         disabled:opacity-50 disabled:cursor-not-allowed
                         text-sm"
            >
              🗑️ Clear Caches
            </button>
            
            <button
              onClick={() => handleAction(browserCache.clearStorage, 'Clear Browser Storage')}
              disabled={loading}
              className="px-4 py-3 bg-yellow-500/20 hover:bg-yellow-500/30 
                         border border-yellow-500/40 text-yellow-400
                         rounded-lg font-medium transition-all duration-200
                         disabled:opacity-50 disabled:cursor-not-allowed
                         text-sm"
            >
              💾 Clear Storage
            </button>
            
            <button
              onClick={() => handleAction(forceReload, 'Force Reload')}
              disabled={loading}
              className="px-4 py-3 bg-editions-green/20 hover:bg-editions-green/30 
                         border border-editions-green/40 text-editions-green
                         rounded-lg font-medium transition-all duration-200
                         disabled:opacity-50 disabled:cursor-not-allowed
                         text-sm"
            >
              ↻ Force Reload
            </button>
          </div>
        </div>

        {/* Nuclear Option */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-red-400 mb-3">⚠️ Nuclear Option</h3>
          <button
            onClick={() => handleAction(nuclearReset, 'Nuclear Reset')}
            disabled={loading}
            className="w-full px-4 py-3 bg-red-500/20 hover:bg-red-500/30 
                       border border-red-500/40 text-red-400
                       rounded-lg font-medium transition-all duration-200
                       disabled:opacity-50 disabled:cursor-not-allowed
                       text-sm"
          >
            💥 Nuclear Reset (Clear Everything + Reload)
          </button>
          <p className="text-xs text-gray-400 mt-2">
            This will clear all caches, storage, unregister service worker, and force reload.
          </p>
        </div>

        {/* Status Message */}
        {message && (
          <div className="mb-4 p-3 bg-editions-dark-light/50 rounded-lg border border-white/10">
            <div className="text-sm text-white">{message}</div>
          </div>
        )}

        {/* Loading Indicator */}
        {loading && (
          <div className="flex items-center justify-center py-4">
            <div className="w-6 h-6 border-2 border-editions-purple/30 border-t-editions-purple rounded-full animate-spin"></div>
            <span className="ml-2 text-gray-400">Processing...</span>
          </div>
        )}

        {/* Instructions */}
        <div className="text-xs text-gray-400 space-y-1">
          <p><strong>Refresh Cache:</strong> Clears and reinstalls all cached assets</p>
          <p><strong>Clear Caches:</strong> Removes all service worker caches</p>
          <p><strong>Clear Storage:</strong> Removes localStorage, sessionStorage, indexedDB</p>
          <p><strong>Force Reload:</strong> Reloads page with cache-busting parameters</p>
          <p><strong>Nuclear Reset:</strong> Complete reset of everything</p>
        </div>
      </div>
    </div>
  )
} 